import { useEffect, useMemo, useState } from 'react';
import {
  fetchPaymentCards,
  mapPaymentCardsToOptions,
  PaymentCard,
  PaymentCardOption,
} from '../../../api/paymentCards';
import {
  CampaignCatalogItem,
  fetchCampaigns,
  fetchProducts,
  fetchTargets,
  fetchZones,
  ProductCatalogItem,
  TargetCatalogItem,
  ZoneCatalogItem,
} from '../../../api/publicationCatalogs';
import { CatalogOption, PublicationMarketplace, SkuOption } from '../types';

const formatCategories = (categories: string[]): string => {
  if (!categories?.length) return '';
  return categories.join(' > ');
};

const mapProductsToSkuOptions = (products: ProductCatalogItem[]): SkuOption[] =>
  products.map((product) => ({
    product_id: product.product_id,
    sku: product.sku,
    name: product.name,
    category: formatCategories(product.categories),
  }));

interface UsePublicationCatalogsParams {
  open: boolean;
  marketplace: PublicationMarketplace;
  selectedCampaignIds: string[];
}

export const usePublicationCatalogs = ({
  open,
  marketplace,
  selectedCampaignIds,
}: UsePublicationCatalogsParams) => {
  const [campaigns, setCampaigns] = useState<CampaignCatalogItem[]>([]);
  const [products, setProducts] = useState<SkuOption[]>([]);
  const [zones, setZones] = useState<ZoneCatalogItem[]>([]);
  const [targets, setTargets] = useState<TargetCatalogItem[]>([]);
  const [paymentCards, setPaymentCards] = useState<PaymentCard[]>([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  const [loadingTargets, setLoadingTargets] = useState(false);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const loadCatalogs = async () => {
      setLoadingCatalogs(true);
      try {
        const [campaignsData, productsData, zonesData, paymentCardsData] = await Promise.all([
          fetchCampaigns(marketplace),
          fetchProducts(marketplace),
          fetchZones(marketplace),
          fetchPaymentCards(),
        ]);
        if (cancelled) return;
        setCampaigns(campaignsData);
        setProducts(mapProductsToSkuOptions(productsData));
        setZones(zonesData);
        setPaymentCards(paymentCardsData);
      } catch {
        if (!cancelled) {
          setCampaigns([]);
          setProducts([]);
          setZones([]);
          setPaymentCards([]);
        }
      } finally {
        if (!cancelled) setLoadingCatalogs(false);
      }
    };

    loadCatalogs();

    return () => {
      cancelled = true;
    };
  }, [open, marketplace]);

  useEffect(() => {
    if (!open) {
      setTargets([]);
      setPaymentCards([]);
      return;
    }

    let cancelled = false;
    const numericIds = selectedCampaignIds.map(Number).filter((id) => !Number.isNaN(id));

    const loadTargets = async () => {
      if (!numericIds.length) {
        setTargets([]);
        return;
      }

      setLoadingTargets(true);
      try {
        const targetsData = await fetchTargets(numericIds, marketplace);
        if (!cancelled) setTargets(targetsData);
      } catch {
        if (!cancelled) setTargets([]);
      } finally {
        if (!cancelled) setLoadingTargets(false);
      }
    };

    loadTargets();

    return () => {
      cancelled = true;
    };
  }, [open, marketplace, selectedCampaignIds]);

  const campaignOptions = useMemo<CatalogOption[]>(
    () =>
      campaigns.map((campaign) => ({
        id: String(campaign.campaign_id),
        label: campaign.name,
      })),
    [campaigns],
  );

  const zoneOptions = useMemo<CatalogOption[]>(
    () =>
      zones.map((zone) => ({
        id: zone.zone_id,
        label: zone.name,
      })),
    [zones],
  );

  const targetOptions = useMemo<CatalogOption[]>(
    () =>
      targets.map((target) => ({
        id: String(target.target_id),
        label: target.name,
      })),
    [targets],
  );

  const paymentCardOptions = useMemo<PaymentCardOption[]>(
    () => mapPaymentCardsToOptions(paymentCards),
    [paymentCards],
  );

  return {
    products,
    campaignOptions,
    zoneOptions,
    targetOptions,
    paymentCards,
    paymentCardOptions,
    loadingCatalogs,
    loadingTargets,
  };
};
